/**
 * Cable Path Service
 *
 * CRUD operations for cable paths on floor plans.
 * Paths represent cable runs between junction boxes or arbitrary points.
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type { Point } from '@/types/common';
import type {
  CablePath,
  CablePathRow,
  CreateCablePath,
  UpdateCablePath,
  CableEntry,
  ConduitConfig,
  PathType,
  PathRoute,
  CableRequirement,
  GenericCableType,
} from '@/types/cablePaths';
import { GENERIC_CABLE_LABELS } from '@/types/cablePaths';
import { calculateDistance, pixelsToUnits } from '@/utils/scaleCalculations';
import type { ScaleData } from '@/types/floorPlanViewer';

/**
 * Parse a database row into a typed CablePath.
 */
function parsePathRow(row: CablePathRow): CablePath {
  return {
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    start_junction_id: row.start_junction_id,
    end_junction_id: row.end_junction_id,
    points: (row.points as Point[]) || [],
    length_real: row.length_real,
    length_unit: row.length_unit ?? 'ft',
    path_type: (row.path_type as PathType) ?? 'conduit',
    route: (row.route as PathRoute) ?? 'ceiling',
    conduit: row.conduit as ConduitConfig | null,
    cables: (row.cables as CableEntry[]) || [],
    vertical_drop_start: row.vertical_drop_start ?? 10,
    vertical_drop_end: row.vertical_drop_end ?? 10,
    vertical_drop_unit: row.vertical_drop_unit ?? 'ft',
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  };
}

/**
 * Fetch all cable paths for a floor plan.
 */
export async function getCablePaths(floorPlanId: string): Promise<CablePath[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('cable_paths')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .order('created_at');

  if (error) throw error;

  return (data ?? []).map(parsePathRow);
}

/**
 * Get a single cable path by ID.
 */
export async function getCablePath(id: string): Promise<CablePath | null> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase.from('cable_paths').select('*').eq('id', id).single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return parsePathRow(data as CablePathRow);
}

/**
 * Create a new cable path.
 */
export async function createCablePath(path: CreateCablePath): Promise<CablePath> {
  const supabase = await getAuthenticatedSupabaseClient();

  const { data, error } = await supabase
    .from('cable_paths')
    .insert({
      floor_plan_id: path.floor_plan_id,
      name: path.name,
      start_junction_id: path.start_junction_id,
      end_junction_id: path.end_junction_id,
      points: path.points,
      length_real: path.length_real,
      length_unit: path.length_unit,
      path_type: path.path_type,
      route: path.route,
      conduit: path.conduit,
      cables: path.cables,
      vertical_drop_start: path.vertical_drop_start,
      vertical_drop_end: path.vertical_drop_end,
      vertical_drop_unit: path.vertical_drop_unit,
      created_by_clerk_id: path.created_by_clerk_id,
    })
    .select()
    .single();

  if (error) throw error;

  return parsePathRow(data as CablePathRow);
}

/**
 * Update a cable path.
 */
export async function updateCablePath(id: string, updates: UpdateCablePath): Promise<CablePath> {
  const supabase = await getAuthenticatedSupabaseClient();

  const { data, error } = await supabase
    .from('cable_paths')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  return parsePathRow(data as CablePathRow);
}

/**
 * Delete a cable path.
 */
export async function deleteCablePath(id: string): Promise<boolean> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase.from('cable_paths').delete().eq('id', id).select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(`Cable path ${id} was not deleted`);
  }
  return true;
}

/**
 * Delete multiple cable paths.
 */
export async function deleteCablePaths(ids: string[]): Promise<boolean> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase.from('cable_paths').delete().in('id', ids).select('id');

  if (error) throw error;
  if ((data?.length ?? 0) !== ids.length) {
    throw new Error(`Deleted ${data?.length ?? 0} of ${ids.length} cable paths`);
  }
  return true;
}

/**
 * Get all paths connected to a specific junction.
 */
export async function getPathsForJunction(junctionId: string): Promise<CablePath[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('cable_paths')
    .select('*')
    .or(`start_junction_id.eq.${junctionId},end_junction_id.eq.${junctionId}`);

  if (error) throw error;

  return (data ?? []).map(parsePathRow);
}

// ============================================
// Length Calculation Utilities
// ============================================

/**
 * Calculate the pixel length of a path, including junction coordinates.
 *
 * @param points - Intermediate vertices of the path
 * @param startJunction - Junction at the start (if any)
 * @param endJunction - Junction at the end (if any)
 * @returns Length in pixels
 */
export function calculatePathPixelLength(
  points: Point[],
  startJunction?: { x: number; y: number } | null,
  endJunction?: { x: number; y: number } | null,
): number {
  const allPoints: Point[] = [];

  // Add start junction if present
  if (startJunction) {
    allPoints.push({ x: startJunction.x, y: startJunction.y });
  }

  // Add intermediate points
  allPoints.push(...points);

  // Add end junction if present
  if (endJunction) {
    allPoints.push({ x: endJunction.x, y: endJunction.y });
  }

  // Need at least 2 points to calculate length
  if (allPoints.length < 2) return 0;

  let totalLength = 0;
  for (let i = 0; i < allPoints.length - 1; i++) {
    totalLength += calculateDistance(allPoints[i], allPoints[i + 1]);
  }

  return totalLength;
}

/**
 * Calculate the real-world length of a path using scale data.
 *
 * @param points - Intermediate vertices of the path
 * @param startJunction - Junction at the start (if any)
 * @param endJunction - Junction at the end (if any)
 * @param scaleData - Floor plan scale data
 * @returns Length in real-world units
 */
export function calculatePathRealLength(
  points: Point[],
  startJunction: { x: number; y: number } | null | undefined,
  endJunction: { x: number; y: number } | null | undefined,
  scaleData: ScaleData | null,
): number {
  const pixelLength = calculatePathPixelLength(points, startJunction, endJunction);
  return pixelsToUnits(pixelLength, scaleData);
}

/**
 * Get all points for a path including junction coordinates.
 * Used for rendering the complete path.
 */
export function getFullPathPoints(
  path: CablePath,
  junctions: Map<string, { x: number; y: number }>,
): Point[] {
  const allPoints: Point[] = [];

  // Add start junction coordinate
  if (path.start_junction_id) {
    const startJunction = junctions.get(path.start_junction_id);
    if (startJunction) {
      allPoints.push({ x: startJunction.x, y: startJunction.y });
    }
  }

  // Add intermediate points
  allPoints.push(...path.points);

  // Add end junction coordinate
  if (path.end_junction_id) {
    const endJunction = junctions.get(path.end_junction_id);
    if (endJunction) {
      allPoints.push({ x: endJunction.x, y: endJunction.y });
    }
  }

  return allPoints;
}

// ============================================
// Cable Aggregation (Reporting)
// ============================================

/**
 * Aggregate cable requirements across multiple paths.
 * Groups cables by type/product and sums total lengths.
 */
export function aggregateCableRequirements(
  paths: CablePath[],
  junctions: Map<string, { x: number; y: number }>,
  scaleData: ScaleData | null,
): CableRequirement[] {
  const requirements = new Map<string, CableRequirement>();

  for (const path of paths) {
    // Calculate path length
    const startJunction = path.start_junction_id ? junctions.get(path.start_junction_id) : null;
    const endJunction = path.end_junction_id ? junctions.get(path.end_junction_id) : null;
    const pathLength = calculatePathRealLength(path.points, startJunction, endJunction, scaleData);

    // Process each cable in the path
    for (const cable of path.cables) {
      // Create unique identifier
      const identifier =
        cable.cable_ref_type === 'product' && cable.product_id
          ? `product:${cable.product_id}`
          : `generic:${cable.generic_type}`;

      // Calculate total length for this cable entry
      const baseLength = pathLength + cable.extra_end_a + cable.extra_end_b;
      const totalLength = baseLength * cable.qty;

      // Get or create requirement entry
      // Use scaleData.unit for consistent units across the report
      const unit = scaleData?.unit ?? path.length_unit ?? 'ft';
      const existing = requirements.get(identifier);
      if (existing) {
        existing.total_length += totalLength;
        existing.path_count += 1;
      } else {
        // Use friendly label from GENERIC_CABLE_LABELS for display
        const friendlyLabel =
          cable.cable_ref_type === 'product'
            ? `Product: ${cable.product_id?.slice(0, 8)}...`
            : (GENERIC_CABLE_LABELS[cable.generic_type as GenericCableType] ??
              cable.generic_type ??
              'Unknown');

        requirements.set(identifier, {
          identifier,
          label: friendlyLabel,
          type: cable.cable_ref_type,
          product_id: cable.product_id,
          generic_type: cable.generic_type,
          total_length: totalLength,
          unit,
          path_count: 1,
        });
      }
    }
  }

  return Array.from(requirements.values());
}

// Export as a service object for consistency
export const cablePathService = {
  getAll: getCablePaths,
  get: getCablePath,
  create: createCablePath,
  update: updateCablePath,
  delete: deleteCablePath,
  deleteMany: deleteCablePaths,
  getForJunction: getPathsForJunction,
  calculatePixelLength: calculatePathPixelLength,
  calculateRealLength: calculatePathRealLength,
  getFullPoints: getFullPathPoints,
  aggregateRequirements: aggregateCableRequirements,
};
